const API_BASE = '/api';
const staticApiKey = import.meta.env.VITE_STATIC_API_KEY;

// Extension communication utilities
// Declare chrome types for web page context (external messaging to extension)
declare const chrome: {
  runtime?: {
    sendMessage: (
      extensionId: string,
      message: unknown,
      callback: (response: unknown) => void
    ) => void;
    lastError?: { message?: string };
  };
} | undefined;

export interface ExtensionResponse {
  success: boolean;
  error?: string;
  extensionId?: string;
  clientId?: string;
}

export function isExtensionAvailable(): boolean {
  const available = !!(typeof chrome !== 'undefined' && chrome?.runtime?.sendMessage);
  console.log('[Extension] isExtensionAvailable:', available, {
    chromeExists: typeof chrome !== 'undefined',
    runtimeExists: typeof chrome !== 'undefined' && !!chrome?.runtime,
    sendMessageExists: typeof chrome !== 'undefined' && !!chrome?.runtime?.sendMessage,
  });
  return available;
}

export async function sendMessageToExtension(
  extensionId: string,
  message: unknown
): Promise<ExtensionResponse> {
  console.log('[Extension] Sending message to extension', { extensionId, message });

  return new Promise((resolve) => {
    if (typeof chrome === 'undefined' || !chrome?.runtime?.sendMessage) {
      console.error('[Extension] Chrome extension API not available');
      resolve({ success: false, error: 'Chrome extension API not available' });
      return;
    }

    try {
      console.log('[Extension] Calling chrome.runtime.sendMessage...');
      chrome.runtime.sendMessage(extensionId, message, (response) => {
        if (chrome?.runtime?.lastError) {
          console.error('[Extension] lastError:', chrome.runtime.lastError);
          resolve({
            success: false,
            error: chrome.runtime.lastError.message || 'Extension not responding',
          });
        } else if (response) {
          console.log('[Extension] Got response:', response);
          resolve(response as ExtensionResponse);
        } else {
          console.warn('[Extension] No response from extension');
          resolve({ success: false, error: 'No response from extension' });
        }
      });
    } catch (error) {
      console.error('[Extension] Exception sending message:', error);
      resolve({
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send message',
      });
    }
  });
}

export async function pingExtension(extensionId: string): Promise<ExtensionResponse> {
  console.log('[Extension] Pinging extension', extensionId);
  const response = await sendMessageToExtension(extensionId, { type: 'PING' });
  console.log('[Extension] Ping result:', response);
  return response;
}

export async function registerClientViaExtension(
  extensionId: string,
  params: {
    apiUrl: string;
    apiKey: string;
    clientName?: string;
  }
): Promise<ExtensionResponse> {
  // Extension generates its own persistent clientId
  return sendMessageToExtension(extensionId, {
    type: 'REGISTER_CLIENT',
    ...params,
  });
}

// Only used to authenticate the dashboard's WebSocket "watcher" connection
// (the WS handler accepts an API key via query param). Regular HTTP calls
// use the Better Auth session cookie via `apiFetch`.
export function getApiKey(): string | null {
  return staticApiKey || null;
}

export async function apiFetch<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (response.status === 401) {
    // Session expired or missing — bounce to login (unless already there).
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

class ApiClient {
  getApiKey(): string | null {
    return getApiKey();
  }

  private async request<T>(
    path: string,
    options: RequestInit = {}
  ): Promise<T> {
    return apiFetch<T>(path, options);
  }

  // Auth
  async bootstrap(name: string) {
    return this.request<{ id: string; key: string; name: string }>('/bootstrap', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async getApiKeys() {
    return this.request<Array<{
      id: string;
      keyPrefix: string;
      name: string;
      createdAt: string;
      lastUsedAt?: string;
    }>>('/auth/keys');
  }

  async createApiKey(name: string) {
    return this.request<{ id: string; key: string; name: string }>('/auth/keys', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async deleteApiKey(id: string) {
    return this.request('/auth/keys/' + id, { method: 'DELETE' });
  }

  // Clients
  async getClients() {
    return this.request<Array<{
      id: string;
      name?: string;
      userAgent?: string;
      lastSeenAt?: string;
      isOnline: boolean;
      actionCount: number;
      createdAt: string;
    }>>('/clients');
  }

  async createClient(data: { id: string; name?: string }) {
    return this.request<{
      id: string;
      name?: string;
      isOnline: boolean;
      createdAt: string;
    }>('/clients/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateClient(id: string, data: { name?: string }) {
    return this.request('/clients/' + id, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteClient(id: string) {
    return this.request('/clients/' + id, { method: 'DELETE' });
  }

  // Actions
  async getActions(clientId?: string) {
    const params = clientId ? `?clientId=${clientId}` : '';
    return this.request<Array<import('@databro/shared').ActionWithStatus>>('/actions' + params);
  }

  async getAction(id: string) {
    return this.request<import('@databro/shared').Action>('/actions/' + id);
  }

  async createAction(data: import('@databro/shared').CreateActionInput) {
    return this.request<import('@databro/shared').Action>('/actions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAction(id: string, data: import('@databro/shared').UpdateActionInput) {
    return this.request<import('@databro/shared').Action>('/actions/' + id, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteAction(id: string) {
    return this.request('/actions/' + id, { method: 'DELETE' });
  }

  async cloneAction(id: string, data: { name: string; clientId?: string }) {
    return this.request<import('@databro/shared').Action>('/actions/' + id + '/clone', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async runAction(id: string, inputs?: Record<string, unknown>) {
    return this.request<{ executionId: string }>('/actions/' + id + '/run', {
      method: 'POST',
      body: JSON.stringify(inputs ? { inputs } : {}),
    });
  }

  async runActionBatch(id: string, params: import('@databro/shared').BatchExecutionRequestInput) {
    return this.request<import('@databro/shared').BatchExecutionResponseOutput>('/actions/' + id + '/run-batch', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  async getActionInputs(id: string) {
    return this.request<{
      inputs: Array<import('@databro/shared').ActionInput>;
      detected: string[];
      configured: Array<import('@databro/shared').ActionInput>;
    }>('/actions/' + id + '/inputs');
  }

  async clearCapturedRequest(id: string) {
    return this.request('/actions/' + id + '/captured-request', {
      method: 'DELETE',
    });
  }

  async triggerCapture(id: string) {
    return this.request<{ success: boolean; message: string }>('/actions/' + id + '/capture', {
      method: 'POST',
    });
  }

  async getCaptureLogs(id: string) {
    return this.request<{
      captureSessionId: string;
      status: string;
      logs: Array<import('@databro/shared').CaptureLogEntry>;
      error?: string;
      startedAt: string;
      completedAt?: string;
    }>('/actions/' + id + '/capture-logs');
  }

  // Capture Sessions
  async getCaptureSession(id: string) {
    return this.request<import('@databro/shared').CaptureSessionWithAction>('/captures/' + id);
  }

  async stopCapture(id: string) {
    return this.request<{ success: boolean }>('/captures/' + id + '/stop', {
      method: 'POST',
    });
  }

  async getCaptureSessions(params?: {
    actionId?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }) {
    const searchParams = new URLSearchParams();
    if (params?.actionId) searchParams.set('actionId', params.actionId);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const query = searchParams.toString();
    return this.request<{
      data: Array<import('@databro/shared').CaptureSessionWithAction>;
      pagination: {
        total: number;
        limit: number;
        offset: number;
        hasMore: boolean;
      };
    }>('/captures' + (query ? '?' + query : ''));
  }

  // Executions
  async getExecutions(params?: {
    actionId?: string;
    clientId?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }) {
    const searchParams = new URLSearchParams();
    if (params?.actionId) searchParams.set('actionId', params.actionId);
    if (params?.clientId) searchParams.set('clientId', params.clientId);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const query = searchParams.toString();
    return this.request<{
      data: Array<import('@databro/shared').ExecutionWithAction>;
      pagination: {
        total: number;
        limit: number;
        offset: number;
        hasMore: boolean;
      };
    }>('/executions' + (query ? '?' + query : ''));
  }

  async getExecution(id: string) {
    return this.request<import('@databro/shared').Execution>('/executions/' + id);
  }

  async retryExecution(id: string) {
    return this.request<{ executionId: string }>('/executions/' + id + '/retry', {
      method: 'POST',
    });
  }

  async cancelExecution(id: string) {
    return this.request('/executions/' + id + '/cancel', { method: 'POST' });
  }

  async retryWebhooks(executionIds: string[]) {
    return this.request<{
      queued: number;
      skipped: number;
      details: Array<{
        executionId: string;
        status: 'queued' | 'skipped';
        deliveryId?: string;
        reason?: string;
      }>;
    }>('/executions/retry-webhooks', {
      method: 'POST',
      body: JSON.stringify({ executionIds }),
    });
  }

  async retryExecutions(executionIds: string[]) {
    return this.request<{
      queued: number;
      skipped: number;
      details: Array<{
        executionId: string;
        status: 'queued' | 'skipped';
        newExecutionId?: string;
        reason?: string;
      }>;
    }>('/executions/retry-bulk', {
      method: 'POST',
      body: JSON.stringify({ executionIds }),
    });
  }

  // Batches
  async getBatches(params?: {
    actionId?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }) {
    const searchParams = new URLSearchParams();
    if (params?.actionId) searchParams.set('actionId', params.actionId);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const query = searchParams.toString();
    return this.request<{
      data: Array<import('@databro/shared').ExecutionBatch & {
        action: { id: string; slug: string; name: string };
      }>;
      pagination: {
        total: number;
        limit: number;
        offset: number;
        hasMore: boolean;
      };
    }>('/batches' + (query ? '?' + query : ''));
  }

  async getBatch(id: string) {
    return this.request<import('@databro/shared').ExecutionBatchWithExecutions & {
      action: { id: string; slug: string; name: string; platform: string };
    }>('/batches/' + id);
  }

  async retryBatchFailed(id: string) {
    return this.request<{ retriedCount: number; executionIds: string[] }>(
      '/batches/' + id + '/retry-failed',
      { method: 'POST' }
    );
  }

  // Client detail
  async getClient(id: string) {
    return this.request<{
      id: string;
      name: string | null;
      email: string | null;
      userAgent: string | null;
      lastSeenAt: string | null;
      isOnline: boolean;
      createdAt: string;
      actions: Array<{
        id: string;
        slug: string;
        name: string;
        platform: string;
        enabled: boolean;
      }>;
    }>('/clients/' + id);
  }

  // Client action assignments
  async assignActionsToClient(clientId: string, actionIds: string[]) {
    return this.request<{ success: boolean; assigned: number }>(
      '/clients/' + clientId + '/actions',
      {
        method: 'POST',
        body: JSON.stringify({ actionIds }),
      }
    );
  }

  async removeActionFromClient(clientId: string, actionId: string) {
    return this.request<{ success: boolean }>(
      '/clients/' + clientId + '/actions/' + actionId,
      { method: 'DELETE' }
    );
  }

  // Action client assignments
  async assignClientsToAction(actionId: string, clientIds: string[]) {
    return this.request<{ success: boolean; assigned: number }>(
      '/actions/' + actionId + '/clients',
      {
        method: 'POST',
        body: JSON.stringify({ clientIds }),
      }
    );
  }

  async removeClientFromAction(actionId: string, clientId: string) {
    return this.request<{ success: boolean }>(
      '/actions/' + actionId + '/clients/' + clientId,
      { method: 'DELETE' }
    );
  }

  // === Recordings ===
  async getRecordings(params?: { limit?: number; offset?: number }) {
    const q = new URLSearchParams();
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.offset) q.set('offset', String(params.offset));
    const qs = q.toString();
    return this.request<{
      data: Array<import('@databro/shared').Recording & { responseCount?: number }>;
      pagination: { total: number; limit: number; offset: number; hasMore: boolean };
    }>('/recordings' + (qs ? '?' + qs : ''));
  }

  async getRecording(id: string) {
    return this.request<
      import('@databro/shared').Recording & {
        template?: { id: string; name: string };
        responses: import('@databro/shared').RecordedResponseSummary[];
        endpoints: Array<{ path: string; method: string; hits: number }>;
      }
    >('/recordings/' + id);
  }

  async getRecordingResponses(
    id: string,
    params?: { focus?: string; limit?: number; offset?: number }
  ) {
    const q = new URLSearchParams();
    if (params?.focus) q.set('focus', params.focus);
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.offset) q.set('offset', String(params.offset));
    const qs = q.toString();
    return this.request<{
      data: Array<{ seq: number; url: string; method: string; status: number; response: unknown }>;
      pagination: { total: number; limit: number; offset: number; hasMore: boolean };
    }>('/recordings/' + id + '/responses' + (qs ? '?' + qs : ''));
  }

  async getRecordingMerged(id: string, params: { path: string; dedupeBy?: string; focus?: string }) {
    const q = new URLSearchParams();
    q.set('path', params.path);
    if (params.dedupeBy) q.set('dedupeBy', params.dedupeBy);
    if (params.focus) q.set('focus', params.focus);
    return this.request<{ count: number; items: unknown[] }>(
      '/recordings/' + id + '/merged?' + q.toString()
    );
  }

  async promoteRecording(
    id: string,
    body: import('@databro/shared').PromoteRecordingInput
  ) {
    return this.request<{ actionId: string; slug: string }>('/recordings/' + id + '/promote', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async deleteRecording(id: string) {
    return this.request<{ success: boolean }>('/recordings/' + id, { method: 'DELETE' });
  }

  // === Recording templates (recipes) ===
  async getRecordingTemplates() {
    return this.request<Array<import('@databro/shared').RecordingTemplate>>('/recording-templates');
  }

  async createRecordingTemplate(data: import('@databro/shared').CreateRecordingTemplateInput) {
    return this.request<import('@databro/shared').RecordingTemplate>('/recording-templates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateRecordingTemplate(
    id: string,
    data: import('@databro/shared').UpdateRecordingTemplateInput
  ) {
    return this.request<import('@databro/shared').RecordingTemplate>('/recording-templates/' + id, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteRecordingTemplate(id: string) {
    return this.request<{ success: boolean }>('/recording-templates/' + id, { method: 'DELETE' });
  }
}

export const api = new ApiClient();
